/**
 * The office's own starting values for new quick studies, kept in THIS browser
 * only (localStorage, one entry per signed-in user). Nothing is stored on the
 * server: another computer or browser starts from the platform's values.
 *
 * Only what describes the office, never the customer: consumer type, mounting,
 * tilt, facing, module, inverter, the turnkey prices with their maintenance,
 * and the economics (other works, insurance, land lease, inflation, discount).
 * Every read is checked, so a damaged or old entry falls back field by field.
 */
import { CONSUMER_TYPES, FACINGS, MOUNTING_TYPES, studyEconomics, studyPrices } from "./studyPresets";

const KEY_PREFIX = "pythia.quickStudyDefaults.";

const storageKey = (userId) => `${KEY_PREFIX}${userId || "anonymous"}`;

function storage() {
  try {
    return typeof window !== "undefined" && window.localStorage ? window.localStorage : null;
  } catch {
    return null;   // blocked storage (private mode, policies): nothing is kept
  }
}

/** The saved values, checked; null when there are none (or storage is unavailable). */
export function loadQuickStudyDefaults(userId) {
  let raw = null;
  try {
    raw = storage()?.getItem(storageKey(userId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let saved;
  try {
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!saved || typeof saved !== "object" || Array.isArray(saved)) return null;
  const tilt = Number(saved.tilt);
  const out = {
    prices: studyPrices(saved.prices),
    economics: studyEconomics(saved.economics),
    ...(CONSUMER_TYPES.includes(saved.useCase) ? { useCase: saved.useCase } : {}),
    ...(MOUNTING_TYPES.includes(saved.mounting) ? { mounting: saved.mounting } : {}),
    ...(Number.isFinite(tilt) && tilt >= 0 && tilt < 90 ? { tilt } : {}),
    ...(FACINGS.includes(Number(saved.azimuth)) ? { azimuth: Number(saved.azimuth) } : {}),
    ...(typeof saved.module === "string" && saved.module.trim() ? { module: saved.module.trim() } : {}),
    ...(typeof saved.inverter === "string" ? { inverter: saved.inverter.trim() } : {}),
  };
  return out;
}

/** Saves the office's values from a dialog choice; false when the browser refuses. */
export function saveQuickStudyDefaults(userId, choice) {
  const store = storage();
  if (!store || !choice) return false;
  const value = {
    useCase: choice.useCase,
    mounting: choice.mounting,
    tilt: choice.tilt,
    azimuth: choice.azimuth,
    module: choice.module,
    inverter: choice.inverter || "",
    prices: studyPrices(choice.prices),
    economics: studyEconomics(choice.economics),
  };
  try {
    store.setItem(storageKey(userId), JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** Back to the platform's values. */
export function clearQuickStudyDefaults(userId) {
  try {
    storage()?.removeItem(storageKey(userId));
  } catch { /* nothing to clear */ }
}
